import { BiConformedKey } from './catalog';
import { BiMeasureRef, BiQuerySpec, BiTimeGrain, biMeasureKey } from './query';

// Mirror of the blend part of backend `models/src/bi/query.ts` (its own
// file here: `catalog.ts` already imports `query.ts`).

/** Which merged rows a blend keeps. */
export enum BiBlendJoin {
  /** Every row of the first query; the second's measures where it matches. */
  Left = 'left',
  /** Only keys both queries have. */
  Inner = 'inner',
  /** Every key of either query. */
  Full = 'full',
}

export enum BiCardQueryKind {
  Blend = 'blend',
  Totals = 'totals',
}

/** A key a blend merges on: a conformed key, at a grain for `date`. */
export interface BiBlendKey {
  key: BiConformedKey;
  /** Required on `date`, refused on any other key. */
  grain?: BiTimeGrain;
}

/**
 * Two queries, each aggregated on its own to the `on` keys, merged on the
 * keys' normalized values. Every query's dimensions are exactly the `on`
 * keys, so the merge cannot fan out.
 */
export interface BiBlendSpec {
  kind: BiCardQueryKind.Blend;
  /** Exactly two in v1. */
  queries: BiQuerySpec[];
  on: BiBlendKey[];
  join: BiBlendJoin;
}

/**
 * Several totals, each its own query (none grouped), read as one row: the
 * headline numbers of a KPI group, from as many datasets as they need.
 * Measure columns are prefixed by part, as a blend's are by side.
 */
export interface BiTotalsSpec {
  kind: BiCardQueryKind.Totals;
  /** 2–6 queries, none with a dimension. */
  queries: BiQuerySpec[];
}

/**
 * What a draft or a card queries: one dataset, a blend of two, or totals
 * of several.
 */
export type BiCardQuery = BiQuerySpec | BiBlendSpec | BiTotalsSpec;

/** Queries a blend merges; more sides are a later extension. */
export const kBiBlendSides = 2;
/** The parts a totals query reads, as many as a KPI group shows. */
export const kBiMinTotalsParts = 2;
export const kBiMaxTotalsParts = 6;

export function isBiBlendSpec(query: BiCardQuery): query is BiBlendSpec {
  return (query as BiBlendSpec).kind === BiCardQueryKind.Blend;
}

export function isBiTotalsSpec(query: BiCardQuery): query is BiTotalsSpec {
  return (query as BiTotalsSpec).kind === BiCardQueryKind.Totals;
}

/** A query over one dataset: neither a blend nor totals. */
export function isBiPlainQuery(query: BiCardQuery): query is BiQuerySpec {
  return (query as { kind?: unknown }).kind === undefined;
}

/**
 * The plain queries a card query runs: itself, a blend's sides, or the
 * totals' parts.
 */
export function biCardQuerySides(query: BiCardQuery): BiQuerySpec[] {
  return isBiPlainQuery(query) ? [query] : query.queries;
}

/**
 * Every query the card runs asks for its own number of rows (a top 5): a
 * result cut there is what was asked for, not data left out.
 */
export function isBiCardQueryLimited(query: BiCardQuery): boolean {
  return biCardQuerySides(query).every(side => side.limit !== undefined);
}

/**
 * The result column key of a blend's merge key: the conformed key and its
 * grain (`date__month`, `license_plate`), the same whichever fields carry it.
 */
export function biBlendKeyColumn(on: BiBlendKey): string {
  return on.grain ? `${on.key}__${on.grain}` : on.key;
}

/**
 * The result column key of a blend side's or a totals part's measure:
 * prefixed by the side, 1-based (`q1__amount`, `q2__amount`), so two
 * `amount`s cannot collide.
 */
export function biBlendMeasureKey(side: number, ref: BiMeasureRef): string {
  return `q${side + 1}__${biMeasureKey(ref)}`;
}
